const SECTION_HEADINGS = [
  { key: 'about-company', patterns: ['about the company', 'about us', 'company overview'] },
  { key: 'about-role', patterns: ['about the role', 'about this role', 'role overview', 'position overview'] },
  { key: 'qualifications', patterns: ['qualifications', 'what we are looking for', 'what you bring', 'skills & experience', 'skills and experience'] },
  { key: 'responsibilities', patterns: ['responsibilities', 'key responsibilities', 'what you will do', "what you'll do", 'your responsibilities', 'duties'] },
  { key: 'requirements', patterns: ['requirements'] },
  { key: 'benefits', patterns: ['benefits', 'what we offer', 'perks', 'compensation & benefits', 'compensation and benefits'] },
  { key: 'equal-opportunity', patterns: ['equal opportunity employer', 'equal opportunity', 'eeo statement', 'diversity & inclusion', 'diversity and inclusion'] },
];

const LIST_SECTION_KEYS = new Set(['qualifications', 'responsibilities', 'benefits', 'requirements']);

const formatSectionLabel = (pattern) =>
  pattern
    .split(' ')
    .map((word) => (word === 'us' || word === 'eeo' ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ')
    .replace(/\bEeo\b/g, 'EEO')
    .replace(/\bUs\b/g, 'Us');

const HEADING_LOOKUP = SECTION_HEADINGS.flatMap(({ key, patterns }) =>
  patterns.map((pattern) => ({ key, pattern, label: formatSectionLabel(pattern) }))
).sort((a, b) => b.pattern.length - a.pattern.length);

export const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\u00a0/g, ' ').trim();
};

const normalizePlainText = (html) => {
  if (!html) return '';

  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const hasStructuredHtml = (html) => {
  if (!html) return false;

  // Only trust real heading tags. Lists alone often come from scraped JDs
  // that still need section parsing and leave empty <p> gaps if left as HTML.
  return /<h[1-6][^>]*>/i.test(html);
};

/** Remove empty blocks and collapse runaway breaks that create large vertical gaps. */
export const sanitizeJobDescriptionHtml = (html) => {
  if (!html) return '';

  return html
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<div>(?:\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, '')
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(/<p>\s*(?:<br\s*\/?>\s*)+/gi, '<p>')
    .replace(/(?:<br\s*\/?>\s*)+\s*<\/p>/gi, '</p>')
    .replace(/(<\/p>\s*){2,}/gi, '</p>')
    .trim();
};

const countKnownHeadings = (plainText) => {
  const lines = plainText.split('\n');
  let count = 0;
  let previousLineBlank = true;

  for (const line of lines) {
    const isBlank = line.trim() === '';
    if (!isBlank && isBlockStartHeadingLine(line, previousLineBlank)) {
      count += 1;
    }
    previousLineBlank = isBlank;
  }

  return count;
};

const normalizeHeadingText = (text) => text.toLowerCase().replace(/\s+/g, ' ').trim();

const matchExactHeading = (text) => {
  const normalized = normalizeHeadingText(text);

  for (const entry of HEADING_LOOKUP) {
    if (normalized === entry.pattern) {
      return entry;
    }
  }

  return null;
};

/**
 * Returns heading info only when the line is a standalone section title —
 * whole line, optional trailing colon, or "Heading: first sentence" at line start.
 * Never matches keywords mid-sentence or mid-paragraph.
 */
const parseLineHeading = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const exact = matchExactHeading(trimmed.replace(/:$/, ''));
  if (exact) {
    return { entry: exact, inlineContent: '' };
  }

  const colonIndex = trimmed.indexOf(':');
  if (colonIndex <= 0) return null;

  const beforeColon = trimmed.slice(0, colonIndex).trim();
  const afterColon = trimmed.slice(colonIndex + 1).trim();
  const matched = matchExactHeading(beforeColon);

  if (matched && afterColon) {
    return { entry: matched, inlineContent: afterColon };
  }

  return null;
};

const isBlockStartHeadingLine = (line, previousLineBlank) => {
  const heading = parseLineHeading(line);
  if (!heading) return null;

  const trimmed = line.trim();
  const isWholeLine =
    matchExactHeading(trimmed.replace(/:$/, '')) !== null ||
    (heading.inlineContent === '' && trimmed.endsWith(':'));

  if (isWholeLine) return heading;

  if (previousLineBlank && heading.inlineContent) {
    return heading;
  }

  return null;
};

const parseListItems = (content, sectionKey) => {
  const trimmed = content.trim();
  if (!trimmed) return null;

  const lines = trimmed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  const bulletLines = lines.filter((line) => /^[-•*·▪◦]\s+/.test(line) || /^\d+[.)]\s+/.test(line));
  if (bulletLines.length >= 2) {
    return bulletLines.map((line) => line.replace(/^[-•*·▪◦]\s+|^\d+[.)]\s+/, '').trim());
  }

  if (lines.length >= 2 && LIST_SECTION_KEYS.has(sectionKey)) {
    const looksLikeList = lines.every((line) => line.length < 280);
    if (looksLikeList) {
      return lines.map((line) => line.replace(/^[-•*·▪◦]\s+|^\d+[.)]\s+/, '').trim());
    }
  }

  if (trimmed.includes('•')) {
    const items = trimmed
      .split('•')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    if (items.length >= 2) return items;
  }

  if ((trimmed.match(/;/g) || []).length >= 2 && LIST_SECTION_KEYS.has(sectionKey)) {
    const items = trimmed
      .split(';')
      .map((item) => item.trim().replace(/^[-•*·]\s*/, ''))
      .filter((item) => item.length > 8);
    if (items.length >= 2) return items;
  }

  // Only sentence-split when items read like short bullets, not essay paragraphs.
  if (LIST_SECTION_KEYS.has(sectionKey)) {
    const sentences = trimmed
      .split(/(?<=[.!?])\s+(?=[A-Z(])/)
      .map((sentence) => sentence.trim())
      .filter((sentence) => sentence.length > 12);

    const mostlyShort = sentences.length >= 2 && sentences.every((sentence) => sentence.length <= 160);
    if (mostlyShort) {
      return sentences.map((sentence) => sentence.replace(/\.$/, '').trim() + (sentence.endsWith('.') ? '.' : ''));
    }
  }

  return null;
};

const splitIntoParagraphs = (content) => {
  const blocks = content
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  // Soft line breaks without blank lines stay one paragraph.
  if (blocks.length > 0) return blocks;

  const single = content.replace(/\s+/g, ' ').trim();
  return single ? [single] : [];
};

const buildSection = (entry, rawContent) => {
  const content = rawContent.trim();
  const items = parseListItems(content, entry.key);
  const paragraphs = items ? null : splitIntoParagraphs(content);

  return {
    key: entry.key,
    title: entry.label,
    items: items && items.length > 0 ? items : null,
    paragraphs: paragraphs && paragraphs.length > 0 ? paragraphs : null,
  };
};

const mergeSectionContent = (target, source) => {
  if (source.items?.length) {
    target.items = [...(target.items || []), ...source.items];
    target.paragraphs = null;
  } else if (source.paragraphs?.length) {
    target.paragraphs = [...(target.paragraphs || []), ...source.paragraphs];
  }
};

const deduplicateConsecutiveSections = (sections) => {
  const merged = [];

  for (const section of sections) {
    const last = merged[merged.length - 1];
    if (last && last.key === section.key) {
      if (section.title.length > last.title.length) {
        last.title = section.title;
      }
      mergeSectionContent(last, section);
      continue;
    }
    merged.push({ ...section });
  }

  return merged.filter(
    (section) => (section.items && section.items.length > 0) || (section.paragraphs && section.paragraphs.length > 0)
  );
};

const parsePlainTextSections = (plainText) => {
  const lines = plainText.split('\n');
  const sections = [];
  let currentEntry = null;
  let buffer = [];
  let previousLineBlank = true;

  const flushCurrent = () => {
    if (!currentEntry) return;

    const content = buffer.join('\n').trim();
    if (content) {
      sections.push(buildSection(currentEntry, content));
    } else if (!sections.some((section) => section.key === currentEntry.key)) {
      sections.push(buildSection(currentEntry, ''));
    }

    buffer = [];
  };

  for (const line of lines) {
    const isBlank = line.trim() === '';
    const heading = !isBlank ? isBlockStartHeadingLine(line, previousLineBlank) : null;

    if (heading) {
      flushCurrent();
      currentEntry = heading.entry;
      buffer = heading.inlineContent ? [heading.inlineContent] : [];
      previousLineBlank = false;
      continue;
    }

    if (!isBlank) {
      if (currentEntry) {
        buffer.push(line);
      } else {
        currentEntry = { key: 'intro', pattern: 'overview', label: 'Overview' };
        buffer.push(line);
      }
    }

    previousLineBlank = isBlank;
  }

  flushCurrent();

  if (sections.length === 0 && plainText.trim()) {
    return [
      {
        key: 'overview',
        title: 'Overview',
        items: null,
        paragraphs: splitIntoParagraphs(plainText),
      },
    ];
  }

  return deduplicateConsecutiveSections(sections);
};

export const parseJobDescription = (htmlOrText) => {
  if (!htmlOrText || htmlOrText === 'No description provided') {
    return { mode: 'empty', sections: [], html: null };
  }

  const plainText = normalizePlainText(htmlOrText);
  if (!plainText) {
    return { mode: 'empty', sections: [], html: null };
  }

  // Prefer structured sections whenever known headings are present,
  // even if the source HTML also contains lists.
  const knownHeadingCount = countKnownHeadings(plainText);
  if (knownHeadingCount >= 2) {
    const sections = parsePlainTextSections(plainText);
    if (sections.length >= 2) {
      return { mode: 'structured', sections, html: null };
    }
  }

  if (hasStructuredHtml(htmlOrText)) {
    return {
      mode: 'html',
      sections: [],
      html: sanitizeJobDescriptionHtml(htmlOrText),
    };
  }

  const sections = parsePlainTextSections(plainText);

  if (sections.length === 0) {
    return {
      mode: 'html',
      sections: [],
      html: sanitizeJobDescriptionHtml(htmlOrText),
    };
  }

  if (sections.length === 1 && sections[0].key === 'overview' && (sections[0].paragraphs || []).length <= 1) {
    return {
      mode: 'html',
      sections: [],
      html: sanitizeJobDescriptionHtml(htmlOrText),
    };
  }

  return { mode: 'structured', sections, html: null };
};
